import { Button, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { convertBlobToBase64 } from "./utils";
import { useSnackbar } from "notistack";
import { fetchSTTWhisper, fetchSTTShunya } from "../../APIS/clientAPIS";
import {
  Backdrop,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  Chip,
  Box,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Mic, Pause, PlayArrow, Stop } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
// import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// Constants

const CHUNK_INTERVAL = 2* 60 * 1000; // 2 min

const NewAudioVisualizer = ({ transcription, state }) => {
  // Refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const sourceRef = useRef(null);
  const animationIdRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingLive, setRecordingLive] = useState(false);
  const [waitingTranscriptionData, setWaitingTranscriptionData] =
    useState(false);
  const [audioSource, setAudioSource] = useState("default");
  const [timer, setTimer] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  // Detect Safari
  const isSafari = () =>
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Get supported MIME type
  const getSupportedMimeType = () =>
    isSafari() ? "audio/mp4" : "audio/webm;codecs=opus";

  // Safely close AudioContext to prevent InvalidStateError
  const safeCloseAudioContext = (audioContext) => {
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close();
    }
  };

  // Audio Utility Functions
  const validateAudioFile = (file) => {
    const validTypes = ['audio/wav', 'audio/mp3', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/webm'];
    const maxSize = 100 * 1024 * 1024; // 100MB limit
    
    if (!file) {
      throw new Error('No file provided');
    }
    
    if (file instanceof File) {
      if (!validTypes.includes(file.type)) {
        throw new Error(`Invalid file type: ${file.type}. Supported types: ${validTypes.join(', ')}`);
      }
      
      if (file.size > maxSize) {
        throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size: 100MB`);
      }
    }
    
    return true;
  };

  const convertBase64ToFile = (base64Data, filename = 'audio.wav', mimeType = 'audio/wav') => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      throw new Error(`Failed to convert base64 to file: ${error.message}`);
    }
  };

  const getTranscript = async (
    appointment_id,
    user_id,
    expert_id,
    appointment_date,
    booking_date,
    audio_data
  ) => {
    const transcriptHeaders = {
      accept: "application/json",
    };

    const formData = new FormData();
    formData.append("audio_data_base64", audio_data);
    formData.append("session_id", `${appointment_id}`);
    formData.append("appointment_id", `${appointment_id}`);
    formData.append("user_id", `${user_id}`);
    formData.append("expert_id", `${expert_id}`);
    formData.append("appointment_date", `${appointment_date}`);
    formData.append("booking_date", `${booking_date}`);
    formData.append("speaker_id", `${expert_id}`);

    try {
      const response = await fetch(`https://newtts.uwc.world/transcribe`, {
        method: "POST",
        headers: transcriptHeaders,
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const postResponse = await response.json();
      console.log("postResponse", postResponse);

      return postResponse;
    } catch (error) {
      console.error("Error:", error);
      return error;
    }
  };

  // Initialize Audio
  const initAudio = async () => {
    try {
      // Configure audio constraints based on selected source
      const audioConstraints = audioSource === "shunya" 
        ? {
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false,
              sampleRate: 48000,
              channelCount: 1
            }
          }
        : { audio: true }; // Default constraints

      const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      streamRef.current = stream;

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
    } catch (error) {
      console.error("Error initializing audio:", error);
      enqueueSnackbar("Microphone access denied.", { variant: "error" });
    }
  };

  // Start Recording
  const startRecording = async () => {
    try {
      if (!streamRef.current) {
        await initAudio();
      }

      audioChunksRef.current = [];
      const mimeType = getSupportedMimeType();

      mediaRecorderRef.current = new MediaRecorder(streamRef.current, {
        mimeType,
      });

      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = sendChunkedAudio;

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingLive(true);
      setTimer(0);
      startTimer();
      visualize();
    } catch (error) {
      console.error("Error starting recording:", error);
      enqueueSnackbar("Error starting recording.", { variant: "error" });
    }
  };

  // Stop Recording and Restart
  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingLive(false);
      setWaitingTranscriptionData(true);
      cancelAnimationFrame(animationIdRef.current);
      clearCanvas();
    }
  };

  // Function to clear the canvas
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const canvasCtx = canvas.getContext("2d");
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };
  const tempStopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      cancelAnimationFrame(animationIdRef.current);
    }
  };

  // Send Chunked Audio
  const sendChunkedAudio = async () => {
    if (!mediaRecorderRef.current || audioChunksRef.current.length === 0)
      return;

    try {
      const mimeType = getSupportedMimeType();
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
      const base64String = await convertBlobToBase64(audioBlob);

      const formattedBase64 = base64String.split(";base64,")[1];

      if (formattedBase64) {
        let transcriptionResult = "";
        
        // Use different APIs based on selected audio source
        if (audioSource === "shunya") {
          transcriptionResult = await fetchSTTShunya(
            JSON.stringify({ base64_audio: formattedBase64 })
          );
        } else {
          // Default - use current system (Whisper)
          transcriptionResult = await fetchSTTWhisper(
            JSON.stringify({ base64_audio: formattedBase64 })
          );
        }
        
        transcription(transcriptionResult || "");
        setWaitingTranscriptionData(false);
        // enqueueSnackbar("Chunk uploaded successfully.", { variant: "success" });
      }
    } catch (error) {
      console.error("Error sending chunked audio:", error);
      enqueueSnackbar(
        `Error processing audio with ${audioSource === "shunya" ? "Shunya" : "default"} API.`,
        { variant: "error" }
      );
    }

    audioChunksRef.current = []; // Clear for next chunk
  };

  // Timer Logic
  const startTimer = () => {
    timerIntervalRef.current = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);
  };

  const visualize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasCtx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    if (!analyser) return;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationIdRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Clear the canvas
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      // Set bar width and spacing
      const barWidth = 2; // Thin bars
      const totalBarWidth = barWidth * bufferLength; // Total width of all bars
      const offsetX = (canvas.width - totalBarWidth) / 2; // Center the bars horizontally

      const centerY = canvas.height / 2; // The center line for the bars

      let x = offsetX; // Start drawing from the offset to center the bars

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = dataArray[i] / 2; // Scale the bar height

        // Set the color of the bars
        canvasCtx.fillStyle = `#D92D20`;

        // Draw the bar above and below the center line
        canvasCtx.fillRect(x, centerY - barHeight / 2, barWidth, barHeight);

        // Move to the next bar position
        x += barWidth + 1; // Add 1px space between bars
      }
    };

    draw();
  };

  // Automatically stop and restart recording every 30 seconds
  useEffect(() => {
    if (isRecording) {
      const interval = setInterval(() => {
        tempStopRecording();
        setTimeout(() => startRecording(), 300); // Short delay before restarting
      }, CHUNK_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [isRecording]);

  // Reinitialize audio when source changes
  useEffect(() => {
    if (streamRef.current) {
      // Stop current stream
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      safeCloseAudioContext(audioContextRef.current);
      audioContextRef.current = null;
    }
    // Note: Audio will be reinitialized on next recording start
  }, [audioSource]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        safeCloseAudioContext(audioContextRef.current);
      }
      cancelAnimationFrame(animationIdRef.current);
      clearInterval(timerIntervalRef.current);
    };
  }, []);

  return (
    <div>
      {state === "editor" && (
        <div className="d-flex flex-column align-items-center position-relative">
          {/* AI Engine Selector - Positioned at extreme right */}
          {!recordingLive && (
            <div className="position-absolute" style={{ 
              right: '10px', 
              top: '10px', 
              zIndex: 1000 
            }}>
              <Tooltip 
                title="Select Speech Recognition Model"
                placement="top"
                arrow
              >
                <Box>
                  <Select
                        value={audioSource}
                        onChange={(e) => setAudioSource(e.target.value)}
                        IconComponent={({ className }) => (
                          <KeyboardArrowDownIcon
                            className={className}
                            sx={{ 
                              color: "#667eea",
                              fontSize: "20px"
                            }}
                          />
                        )}
                        renderValue={(value) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {value === 'shunya' ? (
                              <>
                                <SmartToyIcon sx={{ fontSize: '16px', color: '#667eea' }} />
                                <span style={{ fontWeight: 600, color: '#667eea' }}>Advanced ASR Version</span>
                              </>
                            ) : (
                              <>
                                <RecordVoiceOverIcon sx={{ fontSize: '16px', color: '#6c757d' }} />
                                <span style={{ fontWeight: 500, color: '#6c757d' }}> Standard Version</span>
                              </>
                            )}
                          </Box>
                        )}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "12px",
                          minWidth: "140px",
                          height: "40px",
                          boxShadow: "0 2px 8px rgba(102, 126, 234, 0.15)",
                          border: "1px solid rgba(102, 126, 234, 0.2)",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(102, 126, 234, 0.02)",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.25)",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(102, 126, 234, 0.05)",
                            boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          },
                          "& .MuiSelect-select": {
                            padding: "8px 40px 8px 12px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "right",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "right",
                          },
                          PaperProps: {
                            sx: {
                              borderRadius: "12px",
                              mt: 1,
                              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              border: "1px solid rgba(102, 126, 234, 0.1)",
                              "& .MuiMenuItem-root": {
                                padding: "12px 16px",
                                borderRadius: "8px",
                                margin: "4px 8px",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: "rgba(102, 126, 234, 0.08)",
                                  transform: "translateY(-1px)",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "rgba(102, 126, 234, 0.12)",
                                  fontWeight: 600,
                                  "&:hover": {
                                    backgroundColor: "rgba(102, 126, 234, 0.16)",
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="default">
                          <RecordVoiceOverIcon sx={{ color: '#6c757d', fontSize: '20px' }} />
                          <Box>
                            <div style={{ fontWeight: 500, color: '#495057' }}>Standard Version</div>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Core speech model</div>
                          </Box>
                        </MenuItem>
                        <MenuItem value="shunya">
                          <SmartToyIcon sx={{ color: '#667eea', fontSize: '20px' }} />
                          <Box>
                            <div style={{ fontWeight: 600, color: '#667eea' }}>Advanced Version</div>
                            <div style={{ fontSize: '12px', color: '#667eea' }}>Enhanced speech engine with high accuracy recognition</div>
                          </Box>
                          <Chip 
                            label="AI" 
                            size="small" 
                            sx={{ 
                              background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '10px',
                              height: '20px',
                              ml: 'auto'
                            }} 
                          />
                        </MenuItem>
                      </Select>
                    </Box>
                  </Tooltip>
                </div>
              )}
          
          {/* Main Content Container */}
          <div
            style={{
              background: "white",
              width: "100%",
              boxShadow: "0px 20px 50px 0px var(--primary-accent-color)",
            }}
            className="row row-cols-3 mb-4 w-100 rounded-bottom border-top py-2"
          >
            <div className="d-flex justify-content-center align-items-center">
              <canvas
                ref={canvasRef}
                style={{
                  height: "30px",
                  width: "100%",
                }}
              ></canvas>
            </div>

            <div className="d-flex justify-content-center gap-2 align-items-center">
              <IconButton
                sx={{
                  backgroundColor: "var(--primary-color)",
                  textTransform: "capitalize",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "var(--primary-color-darken)",
                  },
                }}
                id="notetaker_record_button"
                onClick={startRecording}
              >
                <Mic />
              </IconButton>
              
              {recordingLive && (
                <Button
                  id="notetaker_stop_record_button"
                  className=" rounded-3 px-2"
                  sx={{
                    backgroundColor: "red",
                    textTransform: "capitalize",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "red",
                    },
                  }}
                  onClick={stopRecording}
                >
                  <Stop /> Stop
                </Button>
              )}
            </div>
            <div className="d-flex justify-content-center align-items-center">
              {/* {isRecording && <>{formatTime(timer)}/60:00</>}{" "} */}
              {/* {audioBlob && (
            <Button
              sx={{
                backgroundColor: "white",
                textTransform: "capitalize",
                color: "black",
                "&:hover": {
                  backgroundColor: "white",
                },
              }}
              className=" rounded-3"
              startIcon={<HighlightOff />}
              onClick={clearRecording}
            >
              Cancel
            </Button>
          )} */}
            </div>
          </div>
        </div>
      )}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={waitingTranscriptionData}
      >
        <CircularProgress size={24} color="inherit" />
      </Backdrop>
    </div>
  );
};

export default NewAudioVisualizer;
